import React, { useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";

import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import CustomPaginator from "../../components/CustomPaginator";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Dropdown } from "primereact/dropdown";
import { useTheme } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { CustomButton } from "../../components/CustomButton";
import { OverlayPanel } from "primereact/overlaypanel";
import { Toast } from "primereact/toast";
import { useDispatch, useSelector } from "react-redux";
import { Text } from "../../components/Text";
import { Calendar } from "primereact/calendar";
import { API_SEND_REMINDER } from "../../api/meeting.service";
import {
  getAllMeetingInfo,
  getOneMeetingInfo,
  changePage,
  changelimit,
  cancelMeetingInfo,
} from "../../reducers/meetingSlice";
import TableActionsContainer from "../../components/CustomTable/TableActionsContainer";

const DateRangePicker = ({
  fromDate = "",
  toDate = "",
  handleFromDate = () => {},
  handleToDate = () => {},
  reset = true,
}) => {
  // Calculate one month before and one month after the current date
  const today = new Date();
  const oneMonthBefore = new Date();
  oneMonthBefore.setMonth(today.getMonth() - 1);
  const oneMonthAhead = new Date();
  oneMonthAhead.setMonth(today.getMonth() + 1);

  const resetDate = () => {
    handleFromDate("");
    handleToDate("");
  };

  return (
    <div className="flex align-items-center gap-2 flex-grow-0 sm:flex-grow-1 md:flex-grow-1">
      <div className="flex justify-content-between align-items-center gap-2">
        <Text type={"sub-heading"}>From</Text>
        <Calendar
          showIcon={true}
          placeholder="DD/MM/YYYY"
          dateFormat="dd/mm/yy"
          className={`sklbt-calender `}
          value={fromDate}
          onChange={(e) => handleFromDate(e.value)}
          minDate={oneMonthBefore} // Set the minimum selectable date
          maxDate={oneMonthAhead} // Set the maximum selectable date
        ></Calendar>
      </div>
      <div className="flex  justify-content-between align-items-center gap-2">
        <Text type={"sub-heading"}>To</Text>
        <Calendar
          showIcon={true}
          placeholder="DD/MM/YYYY"
          dateFormat="dd/mm/yy"
          className={`sklbt-calender `}
          value={toDate}
          onChange={(e) => handleToDate(e.value)}
          minDate={fromDate || oneMonthBefore} // Ensure 'To' date is not before 'From' date, or one month before today
          maxDate={oneMonthAhead} // Set the maximum selectable date
        ></Calendar>
      </div>
      {(fromDate || toDate) && reset && (
        <i className="pi pi-times cursor-pointer" onClick={resetDate} />
      )}
    </div>
  );
};

const MeetingScheduler = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const toastRef = useRef(null);
  const opRef = useRef(null);
  const [view, setView] = useState("list");
  const [selectedRow, setSelectedRow] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [tempSearch, setTempSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMeeting, setPopupMeeting] = useState(null);

  const dispatch = useDispatch();
  const { meetings, page, limit, count, isLoading } = useSelector(
    (state) => state.meeting
  );

  let statuses = ["All", "Upcoming", "Completed", "Cancelled"];

  const statusItemTemplate = (option) => {
    return <span className={`customer-badge status-${option}`}>{option}</span>;
  };

  const statusFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={statuses}
        onChange={(e) => {
          options.filterCallback(e.value, options.index);
        }}
        itemTemplate={statusItemTemplate}
        placeholder="Select Status"
        className="p-column-filter"
        showClear
      />
    );
  };
  const updateMeetingStatus = (meeting) => {
    const meetingDateTime = new Date(`${meeting.date} ${meeting.time}`);
    const now = new Date();

    // If meeting has passed and status is not completed or cancelled
    if (meetingDateTime < now && meeting.status.toLowerCase() === "upcoming") {
      return { ...meeting, status: "Completed" };
    }
    return meeting;
  };

  // Sort meeting data by date and time (only for display, actual filtering is on backend)
  const sortedMeetings = [...meetings]
    .map(updateMeetingStatus) // <-- update status if time passed
    .sort(
      (a, b) =>
        new Date(`${a.date} ${a.time}`) - new Date(`${b.date} ${b.time}`)
    );

  // Convert to FullCalendar format
  const formattedEvents = sortedMeetings.map((m) => {
    const start = new Date(`${m.date} ${m.time}`);
    return {
      id: m.id,
      title: m.meetingName,
      start,
      allDay: false,
    };
  });

  useEffect(() => {
    // Convert dates to timestamps for API if they exist
    const startTimestamp = startDate ? startDate.getTime() : null;
    const endTimestamp = endDate ? endDate.getTime() : null;

    dispatch(
      getAllMeetingInfo({
        page,
        limit,
        global: globalFilter,
        startTimestamp,
        endTimestamp,
        status: statusFilter,
      })
    );
  }, [dispatch, page, limit, globalFilter, startDate, endDate, statusFilter]);

  const onPageChange = (event) => {
    const newPage = event.page + 1;
    dispatch(changePage(newPage));
    dispatch(changelimit(event.rows));
  };

  const getAttendeeSummary = (customers = []) => {
    const names = customers.map((c) => c.name);
    const visible = names.slice(0, 3);
    const remaining = names.length - 3;

    return remaining > 0
      ? `${visible.join(", ")}, +${remaining} more`
      : visible.join(", ");
  };

  const getStatusTagSeverity = (status) => {
    switch (status?.toLowerCase()) {
      case "upcoming":
        return "info";
      case "completed":
        return "success";
      case "cancelled":
        return "danger";
      default:
        return null;
    }
  };

  const handleCancelMeeting = async (meeting) => {
    if (!meeting?.id) return;

    try {
      await dispatch(cancelMeetingInfo(meeting.id)).unwrap();
      toastRef.current.show({
        severity: "success",
        summary: "Cancelled",
        detail: `Meeting "${meeting.meetingName}" has been cancelled and participants notified.`,
        life: 3000,
      });
    } catch (err) {
      console.error("Cancellation failed:", err);
      toastRef.current.show({
        severity: "error",
        summary: "Failed",
        detail: err?.error || "Failed to cancel meeting. Try again.",
        life: 4000,
      });
    } finally {
    }
  };

  return (
    <>
      <Toast ref={toastRef} position="top-right" />

      <div
        style={{
          padding: "2rem",
          fontFamily: "Inter, sans-serif",
          backgroundColor: theme.base_background,
          color: theme.text_primary,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <div>
            <h1
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <i
                className="pi pi-calendar"
                style={{ fontSize: "1.8rem", color: theme.primary }}
              />
              Meeting Scheduler
            </h1>
            <p style={{ color: theme.text_secondary }}>
              Book appointments and send automated WhatsApp confirmations &
              reminders
            </p>
          </div>
          <CustomButton
            label="Schedule new meeting"
            icon="pi pi-plus"
            onClick={() => navigate("/meeting/new")}
            style={{
              backgroundColor: theme.primary,
              border: "none",
              padding: "0.6rem 1.2rem",
              color: theme.sideBarText,
            }}
          />
        </div>

        {/* View Toggle and Filters */}
        <div
          style={{
            marginBottom: "1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <div
              style={{
                display: "flex",
                backgroundColor: theme.secondary,
                borderRadius: "6px",
                border: `1px solid ${theme.primary}`,
              }}
            >
              <button
                onClick={() => setView("list")}
                style={{
                  backgroundColor:
                    view === "list" ? theme.background : theme.base_background,
                  color: view === "list" ? theme.primary : theme.text_primary,
                  fontWeight: view === "list" ? "600" : "500",
                  padding: "0.6rem 1rem",
                  flex: 1,
                  border: "none",
                  cursor: "pointer",
                  minWidth: "150px",
                }}
              >
                <i className="pi pi-list" style={{ marginRight: "0.5rem" }}></i>
                List View
              </button>
              <button
                onClick={() => setView("calendar")}
                style={{
                  backgroundColor:
                    view === "calendar"
                      ? theme.background
                      : theme.base_background,
                  color:
                    view === "calendar" ? theme.primary : theme.text_primary,
                  fontWeight: view === "calendar" ? "600" : "500",
                  padding: "0.6rem 1rem",
                  flex: 1,
                  border: "none",
                  cursor: "pointer",
                  minWidth: "150px", // Adjusted for better responsiveness
                }}
              >
                <i
                  className="pi pi-calendar"
                  style={{ marginRight: "0.5rem" }}
                ></i>
                Calendar View
              </button>
            </div>
          </div>

          {/* Search, Date Filters, Status Filter */}
          <div
            style={{
              display: "flex",
              gap: "1rem",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <span className="p-input-icon-right">
              <i
                className="pi pi-search"
                style={{ color: theme.text_secondary }}
              />
              <InputText
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
                onBlur={() => setGlobalFilter(tempSearch)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    setGlobalFilter(tempSearch);
                  }
                }}
                placeholder="Search Appointments"
                style={{
                  width: "100%",
                  padding: "0.5rem 1rem",
                  borderRadius: "8px",
                  border: `1px solid ${theme.text_secondary}`,
                  fontFamily: "Inter",
                  fontSize: "14px",
                  backgroundColor: theme.background,
                  color: theme.text_primary,
                }}
              />
            </span>

            <DateRangePicker
              fromDate={startDate}
              toDate={endDate}
              handleFromDate={setStartDate}
              handleToDate={setEndDate}
            />
          </div>
        </div>

        {/* List View */}
        {view === "list" && (
          <div>
            <DataTable
              value={sortedMeetings}
              showGridlines
              stripedRows
              scrollable
              loading={isLoading} // Show loading state
              emptyMessage="No meetings found."
            >
              <Column
                header="Sl.No"
                body={(rowData, { rowIndex }) => (
                  <span>{String(page + rowIndex + 1).padStart(2, "0")}</span>
                )}
                style={{ width: "60px" }}
              />
              <Column
                header="Meeting Details"
                body={(rowData) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{rowData.meetingName}</div>
                    <div
                      style={{ fontSize: "12px", color: theme.text_secondary }}
                    ></div>
                  </div>
                )}
              />
              <Column field="organizerName" header="Organizer" />
              <Column
                header="Date & Time"
                body={(rowData) => (
                  <div>
                    <div>{rowData.date}</div>
                    <div
                      style={{ fontSize: "12px", color: theme.text_secondary }}
                    >
                      {rowData.time}
                    </div>
                  </div>
                )}
              />
              <Column
                header="People Attending"
                body={(rowData) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{rowData.title}</div>
                    <div
                      style={{ fontSize: "12px", color: theme.text_secondary }}
                    >
                      <i
                        className="pi pi-user"
                        style={{ marginRight: "4px", size: "1rem" }}
                      />
                      {getAttendeeSummary(rowData.customers)}
                    </div>
                  </div>
                )}
              />
              <Column
                header="Status"
                body={(rowData) => (
                  <Tag
                    value={rowData.status}
                    severity={getStatusTagSeverity(rowData.status)}
                    style={{ textTransform: "capitalize" }}
                  />
                )}
                filter
                className="text-sm font-normal"
                showFilterMatchModes={false}
                filterElement={statusFilterTemplate}
                onFilterApplyClick={(e) => {
                  let value = String(
                    e.constraints.constraints[0].value
                  ).toLowerCase();
                  if (value === "all") {
                    setStatusFilter(null);
                  } else {
                    setStatusFilter(value);
                  }
                }}
                onFilterClear={() => setStatusFilter(null)}
                showFilterMenuOptions={false}
              />
              <Column
                header="Actions"
                body={(rowData) => (
                  <Button
                    icon="pi pi-ellipsis-v"
                    className="p-button-text"
                    onClick={(e) => {
                      setSelectedRow(rowData);
                      e.preventDefault();
                      opRef.current.toggle(e);
                    }}
                    style={{ color: theme.text_secondary }}
                  />
                )}
              />
            </DataTable>
            <div className="m-3">
              <TableActionsContainer>
                <div className="flex justify-content-end">
                  <CustomPaginator
                    page={page}
                    limit={limit}
                    changelimit={changelimit}
                    totalRecords={count}
                    changePage={changePage}
                  />
                </div>
              </TableActionsContainer>
            </div>
            <OverlayPanel ref={opRef}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: "160px",
                }}
              >
                {[
                  {
                    label: "View Detail",
                    action: () => navigate(`/meeting/${selectedRow.id}`),
                  },
                  {
                    label: "Reschedule",
                    action: () => navigate(`/meeting/${selectedRow.id}/edit`),
                  },
                  {
                    label: "Send Reminder",
                    action: async () => {
                      if (!selectedRow?.id) return;

                      try {
                        await API_SEND_REMINDER(selectedRow.id);
                        toastRef.current.show({
                          severity: "success",
                          summary: "Reminder Sent",
                          detail: "Reminder has been sent successfully.",
                          life: 3000,
                        });
                      } catch (err) {
                        console.error("Reminder send failed:", err);
                        toastRef.current.show({
                          severity: "error",
                          summary: "Failed",
                          detail:
                            err?.response?.data?.error ||
                            "Failed to send reminder. Try again.",
                          life: 4000,
                        });
                      }
                    },
                  },
                  {
                    label: "Meeting Link 🔗",
                    action: async () => {
                      try {
                        const res = await dispatch(
                          getOneMeetingInfo(selectedRow.id)
                        );

                        const link = res?.payload?.data?.url;
                        // check your backend key

                        if (link) {
                          navigator.clipboard.writeText(link);
                          toastRef.current.show({
                            severity: "success",
                            summary: "Copied!",
                            detail: "Meeting link copied to clipboard",
                            life: 3000,
                          });
                        } else {
                          toastRef.current.show({
                            severity: "warn",
                            summary: "No Link Found",
                            detail:
                              "This meeting does not have a meeting link.",
                            life: 3000,
                          });
                        }
                      } catch (err) {
                        console.error("Error fetching meeting link:", err);
                        toastRef.current.show({
                          severity: "error",
                          summary: "Error",
                          detail: "Failed to fetch meeting link.",
                          life: 3000,
                        });
                      }
                    },
                  },

                  // --- UPDATE THE CANCEL ACTION ---
                  // In the OverlayPanel's actions array
                  {
                    label: "Cancel",
                    action: () => handleCancelMeeting(selectedRow), // This is correct
                  },
                ].map((item, idx) => (
                  <button
                    key={idx}
                    onClick={async () => {
                      // Hide the panel for all actions now
                      if (opRef.current) {
                        opRef.current.hide();
                      }
                      await item.action();
                    }}
                    style={{
                      padding: "0.5rem 1rem",
                      background: "transparent",
                      border: "none",
                      textAlign: "left",
                      width: "100%",
                      fontSize: "14px",
                      color:
                        item.label === "Cancel"
                          ? "#DC2626"
                          : theme.text_primary,
                      cursor: "pointer",
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </OverlayPanel>
          </div>
        )}

        {/* Calendar View */}
        {view === "calendar" && (
          <div style={{ padding: "1rem", borderRadius: "8px" }}>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: "prev today next",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              events={formattedEvents}
              eventClick={(info) => {
                const meeting = sortedMeetings.find(
                  (m) => m.id.toString() === info.event.id
                );
                if (meeting) {
                  setPopupMeeting(meeting);
                  setShowPopup(true);
                }
              }}
              height="auto"
              dayMaxEvents={3}
              eventTimeFormat={{
                hour: "2-digit",
                minute: "2-digit",
                meridiem: "short",
              }}
            />

            <Dialog
              header={popupMeeting?.meetingName}
              visible={showPopup}
              style={{ width: "500px", borderRadius: "12px" }}
              modal
              onHide={() => setShowPopup(false)}
            >
              {popupMeeting && (
                <div
                  style={{
                    fontFamily: "Inter, Arial, sans-serif",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    color: "#111827",
                  }}
                >
                  {/* Date & Time */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "1rem",
                    }}
                  >
                    <i
                      className="pi pi-calendar"
                      style={{ color: "#3B82F6" }}
                    ></i>
                    <span>{popupMeeting.date}</span>
                    <i
                      className="pi pi-clock"
                      style={{ marginLeft: "12px", color: "#3B82F6" }}
                    ></i>
                    <span>{popupMeeting.time}</span>
                  </div>

                  {/* People Attending */}
                  <div style={{ marginBottom: "1rem" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "6px",
                      }}
                    >
                      <i
                        className="pi pi-users"
                        style={{ marginRight: "6px", fontSize: "14px" }}
                      ></i>
                      <span style={{ fontWeight: "500" }}>
                        People Attending
                      </span>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      <span style={{ color: "green", fontWeight: 500 }}>
                        Confirmed: {popupMeeting.confirmedCount || 0}
                      </span>
                      {" | "}
                      <span style={{ color: "orange", fontWeight: 500 }}>
                        Pending: {popupMeeting.pendingCount || 0}
                      </span>
                      {" | "}
                      <span style={{ color: "red", fontWeight: 500 }}>
                        Cancelled: {popupMeeting.cancelledCount || 0}
                      </span>
                    </div>
                    <Dropdown
                      value={null}
                      options={popupMeeting.customers?.map((c) => ({
                        label: c.name,
                        value: c.id,
                      }))}
                      placeholder={`${popupMeeting.customers
                        ?.slice(0, 3)
                        .map((c) => c.name)
                        .join(", ")}${
                        popupMeeting.customers?.length > 3
                          ? ` +${popupMeeting.customers.length - 3}`
                          : ""
                      }`}
                      style={{
                        width: "100%",
                        borderRadius: "6px",
                        fontSize: "13px",
                        padding: "4px",
                      }}
                    />
                  </div>

                  {/* Selected Template */}
                  {popupMeeting.template && (
                    <div style={{ marginBottom: "1rem" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <i
                          className="pi pi-file"
                          style={{ marginRight: "6px", fontSize: "14px" }}
                        ></i>
                        <span style={{ fontWeight: "500" }}>
                          Selected Template
                        </span>
                      </div>
                      <div
                        style={{
                          borderLeft: "4px solid #F97316",
                          background: "#F9FAFB",
                          borderRadius: "6px",
                          padding: "12px",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "6px",
                          }}
                        >
                          <span
                            style={{
                              background: "#F97316",
                              color: "#fff",
                              fontSize: "12px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              marginRight: "8px",
                              fontWeight: "500",
                            }}
                          >
                            Final Reminder
                          </span>
                          <span style={{ fontSize: "12px", color: "#4B5563" }}>
                            (Template Name: {popupMeeting.template.name})
                          </span>
                        </div>
                        <div>
                          <div style={{ marginBottom: "4px" }}>
                            <strong>Hi [Name] 👋</strong>
                          </div>
                          <div>{popupMeeting.template.content}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Booking Type */}
                  <div style={{ marginBottom: "0.5rem" }}>
                    <strong>Booking Type:</strong>{" "}
                    {popupMeeting.bookingType || "N/A"}
                  </div>

                  {/* Organizer */}
                  <div style={{ marginBottom: "0.5rem" }}>
                    <strong>Organizer:</strong> {popupMeeting.organizerName}
                  </div>

                  {/* Reminder Time */}
                  <div
                    style={{
                      marginBottom: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <strong>Reminder Time:</strong>{" "}
                    {popupMeeting.reminderTime || "N/A"} minutes Before
                    <i
                      className="pi pi-bell"
                      style={{ color: "#10B981", fontSize: "14px" }}
                    ></i>
                  </div>

                  {/* Meeting Link */}
                  {popupMeeting.url && (
                    <div style={{ marginTop: "1rem" }}>
                      <strong style={{ fontSize: "14px" }}>Meeting Link</strong>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          background: "#F9FAFB",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          marginTop: "6px",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        <a
                          href={popupMeeting.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#2563EB",
                            textDecoration: "none",
                            flex: 1,
                            fontSize: "13px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={popupMeeting.url}
                        >
                          {popupMeeting.url}
                        </a>
                        <i
                          className="pi pi-copy"
                          style={{
                            cursor: "pointer",
                            color: "#374151",
                            fontSize: "14px",
                            padding: "4px",
                            borderRadius: "4px",
                          }}
                          onClick={() => {
                            navigator.clipboard.writeText(popupMeeting.url);
                            toastRef.current.show({
                              severity: "success",
                              summary: "Copied",
                              detail: "Meeting link copied to clipboard",
                              life: 2000,
                            });
                          }}
                          title="Copy to clipboard"
                        ></i>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Dialog>
          </div>
        )}
        <style>{`
        .fc-button {
          background-color: var(--base_background) !important;
          color: var(--primary) !important;
          border: 1px solid var(--primary) !important;
          font-weight: 500;
          border-radius: 6px;
          padding: 0.4rem 0.8rem;
          transition: 0.2s ease;
        }

        .fc-button:hover,
        .fc-button:focus,
        .fc-button.fc-button-active {
          background-color: var(--primary) !important;
          color: var(--sideBarText) !important;
        }

        .fc .fc-button-group > .fc-button {
          margin-right: 0.4rem;
        }

        .fc {
          font-family: 'Inter', sans-serif;
          background-color: var(--base_background);
          color: var(--text_primary);
        }

        .fc .fc-toolbar-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--text_primary);
        }

        .fc-button {
          background-color: var(--primary);
          color: var(--primary);
          border: 1px solid var(--primary);
          font-weight: 500;
          padding: 0.4rem 0.8rem;
          border-radius: 4px;
        }

        .fc-button.fc-button-active,
        .fc-button:not(:disabled):hover {
          background-color: var(--primary);
          color: var(--sideBarText);
          border-color: var(--primary);
        }

        .fc .fc-daygrid-day-number {
          color: var(--text_secondary);
          font-weight: 500;
          font-size: 0.85rem;
        }

        .fc .fc-col-header-cell-cushion {
          color: var(--text_secondary);
        }

        .fc-event {
          background-color: var(--primary) !important;
          border: none;
          color: var(--sideBarText) !important;
          font-size: 0.8rem;
          padding: 2px 6px;
          border-radius: 4px;
        }

        .fc-day-today {
          background-color: rgba(0, 0, 0, 0.05);
        }

        /* PrimeReact overrides for dark mode compatibility */
        .p-calendar .p-inputtext,
        .p-dropdown .p-inputtext {
            background-color: var(--background) !important;
            color: var(--text_primary) !important;
        }

        .p-calendar .p-button,
        .p-dropdown .p-dropdown-trigger {
            background-color: var(--background) !important;
            color: var(--text_secondary) !important;
        }

        .p-paginator {
          background-color: var(--base_background) !important;
          color: var(--text_primary) !important;
        }

        .p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
          background-color: var(--primary) !important;
          color: var(--sideBarText) !important;
        }

        .p-paginator .p-paginator-page,
        .p-paginator .p-paginator-icon {
          color: var(--text_primary) !important;
        }


        
        .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
          background-color: var(--base_background) !important;
          color: var(--text_primary) !important;
        }

        .p-dropdown-panel .p-dropdown-items .p-dropdown-item:hover {
          background-color: var(--secondary) !important;
        }

        .p-dropdown-panel .p-dropdown-header {
          background-color: var(--base_background) !important;
          color: var(--text_primary) !important;
        }
        `}</style>
      </div>
    </>
  );
};

export default MeetingScheduler;
